/**
 * ============================================================================
 * ADMINISTRATION PAGE: WhatsApp Phone Numbers (`tbl_phone_numbers` - Sheet 16)
 * ============================================================================
 * Purpose:
 *   Administration interface for managing provisioned sending phone numbers,
 *   Meta WABA credentials, Cloud API access tokens, and webhook endpoints
 *   with multi-account and user assignment via a modern right slide-over sidebar drawer.
 *
 * Database Scope:
 *   Tenant Databases & Master Administration
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import { phoneNumberService } from "../../services";
import { accountService, userService } from "../../../master/services";
import { getCurrentUser, isRegularUser } from "../../../core/utils/auth";

// SVG Icons
const MagnifyingGlassIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
    />
  </svg>
);

const FunnelIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
    />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const PencilIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
    />
  </svg>
);

const TrashIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
    />
  </svg>
);

const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
  </svg>
);

const PhoneIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
    />
  </svg>
);

const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
    />
  </svg>
);

const UserCircleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.964 0a9 9 0 1 0-11.963 0m11.964 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
    />
  </svg>
);

export default function PhoneNumberList() {
  const currentUser = useMemo(() => getCurrentUser(), []);
  const isClient = useMemo(() => isRegularUser(currentUser), [currentUser]);

  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAccountId, setFilterAccountId] = useState("all");
  const [filterOperator, setFilterOperator] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [numbersPerPage, setNumbersPerPage] = useState(10);

  // Accounts & Users Metadata
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);

  // Right Sidebar Drawer State
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    int_account_id: "",
    str_phone_number_id: "",
    str_phone_number: "",
    str_waba_id: "",
    str_business_id: "",
    str_operator: "meta",
    str_status: "ACTIVE",
    bool_is_default: false,
    str_wp_token: "",
    str_app_id: "",
    str_webhook_url: "",
  });

  // Load Accounts and Users for binding
  const loadMetadata = useCallback(async () => {
    try {
      const [accRes, userRes] = await Promise.all([
        accountService.get({ limit: 100 }),
        userService.get({ limit: 100 }),
      ]);
      if (accRes && accRes.success) {
        setAccounts(accRes.data || []);
      }
      if (userRes && userRes.success) {
        setUsers(userRes.data || []);
      }
    } catch (err) {
      console.error("Failed to load account/user metadata:", err);
    }
  }, []);

  // Compute structured account options with username & company
  const accountOptions = useMemo(() => {
    return accounts.map((acc) => {
      const accId = acc.int_account_id;
      const code = acc.str_account_code || `ACC${String(accId).padStart(6, "0")}`;
      const matchedUser =
        acc.assigned_user ||
        users.find(
          (u) =>
            String(u.str_account_id) === String(accId) ||
            Number(u.int_user_id) === Number(accId)
        );
      const username = matchedUser?.str_username ? `@${matchedUser.str_username}` : "";
      const name = matchedUser?.str_name || acc.str_company_name || "";
      const label = `${code} ${username ? `· ${username}` : ""} ${name ? `(${name})` : ""}`.trim();

      return {
        value: accId,
        code,
        username: matchedUser?.str_username || "",
        name,
        email: matchedUser?.str_email || "",
        company: acc.str_company_name || "",
        label,
      };
    });
  }, [accounts, users]);

  // Find currently selected account details for drawer preview
  const selectedAccountDetails = useMemo(() => {
    if (!formData.int_account_id) return null;
    return accountOptions.find(
      (opt) => String(opt.value) === String(formData.int_account_id)
    ) || null;
  }, [formData.int_account_id, accountOptions]);

  // Load Phone Numbers from Backend
  const loadNumbers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await phoneNumberService.get({
        limit: 500,
        accountId: isClient ? (currentUser?.str_account_id || currentUser?.int_account_id) : "all",
      });
      if (res && res.success) {
        setNumbers(Array.isArray(res.data) ? res.data : []);
      } else if (Array.isArray(res)) {
        setNumbers(res);
      }
    } catch (err) {
      console.error("Failed to load numbers:", err);
      setNumbers([]);
    } finally {
      setLoading(false);
    }
  }, [isClient, currentUser]);

  useEffect(() => {
    loadMetadata();
    loadNumbers();
  }, [loadMetadata, loadNumbers]);

  const resetForm = () => {
    const defaultAccId = accountOptions.length > 0 ? accountOptions[0].value : "";
    setFormData({
      int_account_id: defaultAccId,
      str_phone_number_id: "",
      str_phone_number: "",
      str_waba_id: "",
      str_business_id: "",
      str_operator: "meta",
      str_status: "ACTIVE",
      bool_is_default: false,
      str_wp_token: "",
      str_app_id: "",
      str_webhook_url: "",
    });
    setSelectedNumber(null);
  };

  const handleOpenAdd = () => {
    if (isClient) return;
    resetForm();
    if (accountOptions.length > 0) {
      setFormData((prev) => ({
        ...prev,
        int_account_id: accountOptions[0].value,
      }));
    }
    setShowDrawer(true);
  };

  const handleEdit = (row) => {
    if (isClient) return;
    setSelectedNumber(row);
    const targetAccountId =
      row.int_account_id ||
      row.account_info?.int_account_id ||
      (accountOptions.length > 0 ? accountOptions[0].value : 1);

    setFormData({
      int_account_id: targetAccountId,
      str_phone_number_id: row.str_phone_number_id || "",
      str_phone_number: row.str_phone_number || "",
      str_waba_id: row.str_waba_id || "",
      str_business_id: row.str_business_id || "",
      str_operator: row.str_operator || "meta",
      str_status: row.str_status || "ACTIVE",
      bool_is_default: Boolean(row.bool_is_default),
      str_wp_token: row.str_wp_token || row.str_token || "",
      str_app_id: row.str_app_id || "",
      str_webhook_url: row.str_webhook_url || "",
    });
    setShowDrawer(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.int_account_id) {
      Swal.fire({
        title: "Account Required",
        text: "Please select an account/user to bind this WhatsApp phone number.",
        icon: "warning",
        confirmButtonColor: "#059669",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        int_account_id: Number(formData.int_account_id),
        str_phone_number_id: (formData.str_phone_number_id || "").trim(),
        str_phone_number: (formData.str_phone_number || "").trim(),
        str_waba_id: (formData.str_waba_id || "").trim(),
        str_business_id: (formData.str_business_id || "").trim(),
        str_operator: (formData.str_operator || "meta").trim(),
        str_status: formData.str_status || "ACTIVE",
        bool_is_default: Boolean(formData.bool_is_default),
        str_wp_token: (formData.str_wp_token || formData.str_token || "").trim(),
        str_app_id: (formData.str_app_id || "").trim(),
        str_webhook_url: (formData.str_webhook_url || "").trim(),
      };

      if (selectedNumber) {
        const id = selectedNumber._id || selectedNumber.id || selectedNumber.str_phone_number_id;
        const res = await phoneNumberService.edit(id, payload);
        if (res && res.success !== false) {
          setShowDrawer(false);
          resetForm();
          await Swal.fire({
            title: "Updated Successfully!",
            text: `Phone number ${formData.str_phone_number} has been updated.`,
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          loadNumbers();
        } else {
          Swal.fire("Error", res?.error || "Failed to update phone number", "error");
        }
      } else {
        const res = await phoneNumberService.create(payload);
        if (res && (res.success || res._id)) {
          setShowDrawer(false);
          resetForm();
          await Swal.fire({
            title: "Registered Successfully!",
            text: `WhatsApp sender ${formData.str_phone_number} is provisioned for Account #${formData.int_account_id}.`,
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
          loadNumbers();
        } else {
          Swal.fire("Error", res?.error || "Failed to register phone number", "error");
        }
      }
    } catch (err) {
      Swal.fire("Error", err.message || "An unexpected error occurred", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    const id = row._id || row.id || row.str_phone_number_id;
    const phone = row.str_phone_number || "this phone number";
    const accountCode =
      row.account_info?.str_account_code ||
      `ACC${String(row.int_account_id || 1).padStart(6, "0")}`;
    const username = row.account_info?.str_username
      ? `@${row.account_info.str_username}`
      : "";

    const result = await Swal.fire({
      title: "Decommission Phone Number?",
      html: `Are you sure you want to decommission sender number <b>${phone}</b> provisioned for <b>${accountCode} ${username}</b>?<br/><br/><span style="font-size:12px;color:#ef4444">This will deactivate routing for incoming & outgoing messages on this number.</span>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Decommission",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await phoneNumberService.delete(id, {
        headers: { "x-account-id": row.int_account_id },
      });
      if (res && res.success !== false) {
        await Swal.fire({
          title: "Decommissioned!",
          text: `Phone number ${phone} has been decommissioned.`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        loadNumbers();
      } else {
        Swal.fire("Error", res?.error || "Failed to delete phone number", "error");
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to delete phone number", "error");
    }
  };

  // Filtering
  const getFilteredNumbers = () => {
    let filtered = numbers;

    if (isClient) {
      const userAccId = String(currentUser?.str_account_id || "");
      const userUname = (currentUser?.str_username || "").toLowerCase();
      const hasOtherAccounts = filtered.some((n) => {
        const acc = String(n.int_account_id || "");
        return userAccId && acc && acc !== userAccId;
      });
      if (hasOtherAccounts) {
        filtered = filtered.filter((n) => {
          const accMatch = userAccId && String(n.int_account_id) === userAccId;
          const unameMatch = userUname && (n.account_info?.str_username || "").toLowerCase() === userUname;
          return accMatch || unameMatch;
        });
      }
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((n) => {
        const phone = (n.str_phone_number || "").toLowerCase();
        const phoneId = (n.str_phone_number_id || "").toLowerCase();
        const waba = (n.str_waba_id || "").toLowerCase();
        const op = (n.str_operator || "").toLowerCase();
        const code = (
          n.account_info?.str_account_code ||
          `ACC${String(n.int_account_id || "")}`
        ).toLowerCase();
        const uname = (n.account_info?.str_username || "").toLowerCase();
        const realName = (n.account_info?.str_name || "").toLowerCase();
        return (
          phone.includes(q) ||
          phoneId.includes(q) ||
          waba.includes(q) ||
          op.includes(q) ||
          code.includes(q) ||
          uname.includes(q) ||
          realName.includes(q)
        );
      });
    }

    if (!isClient && filterAccountId !== "all") {
      filtered = filtered.filter(
        (n) => String(n.int_account_id) === String(filterAccountId)
      );
    }

    if (filterOperator !== "all") {
      filtered = filtered.filter(
        (n) => (n.str_operator || "meta").toLowerCase() === filterOperator.toLowerCase()
      );
    }

    if (filterStatus !== "all") {
      filtered = filtered.filter(
        (n) => (n.str_status || "ACTIVE").toUpperCase() === filterStatus.toUpperCase()
      );
    }

    return filtered;
  };

  // Stats calculation aligned with filtered view
  const filteredNumbers = getFilteredNumbers();
  const getStats = () => {
    const list = filteredNumbers;
    const total = list.length;
    const active = list.filter(
      (n) => (n.str_status || "ACTIVE").toUpperCase() === "ACTIVE"
    ).length;
    const chatbot = list.filter((n) => n.bool_bot).length;
    const meta = list.filter(
      (n) => (n.str_operator || "meta").toLowerCase() === "meta"
    ).length;
    return { total, active, chatbot, meta };
  };

  const totalPages = Math.ceil(filteredNumbers.length / numbersPerPage) || 1;
  const startIndex = (currentPage - 1) * numbersPerPage;
  const paginatedNumbers = filteredNumbers.slice(startIndex, startIndex + numbersPerPage);
  const stats = getStats();

  if (loading && numbers.length === 0) {
    return (
      <div className="py-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading sending numbers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header section with Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-blue-600 bg-clip-text text-transparent mb-2">
              {isClient ? "My Assigned Phone Numbers" : "Phone Numbers"}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
              {isClient
                ? "Verified WhatsApp sender phone numbers provisioned for your account"
                : "Manage verified sender phone numbers, WhatsApp Business Account IDs, and gateway credentials across tenant accounts"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => {
                loadMetadata();
                loadNumbers();
              }}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-sm shadow-blue-500/20 text-xs sm:text-sm cursor-pointer"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              Sync Numbers
            </button>
            {!isClient && (
              <button
                onClick={handleOpenAdd}
                className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-sm shadow-green-500/20 text-xs sm:text-sm cursor-pointer"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Phone Number
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Total Numbers</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Active Senders</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-purple-600">{stats.chatbot}</div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Chatbot Active</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-blue-600">{stats.meta}</div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">Meta Gateway</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search phone numbers, username, account, WABA ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          {/* Account / User Filter */}
          {!isClient && (
            <div className="relative min-w-[190px]">
              <select
                value={filterAccountId}
                onChange={(e) => {
                  setFilterAccountId(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All Accounts / Users</option>
                {accountOptions.map((acc) => (
                  <option key={acc.value} value={acc.value}>
                    {acc.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Gateway Provider Filter */}
          <div className="relative">
            <FunnelIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={filterOperator}
              onChange={(e) => {
                setFilterOperator(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 capitalize"
            >
              <option value="all">All Providers</option>
              <option value="meta">Meta Cloud API</option>
              <option value="karix">Karix</option>
              <option value="pinnacle">Pinnacle</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value="all">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>

          {/* Per-page Selector */}
          <div className="relative">
            <select
              value={numbersPerPage}
              onChange={(e) => {
                setNumbersPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Phone Numbers Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {paginatedNumbers.length === 0 ? (
          <div className="py-16 px-4 text-center">
            <div className="w-16 h-16 rounded-2xl bg-green-50 dark:bg-gray-700 text-green-600 dark:text-green-400 mx-auto flex items-center justify-center mb-4">
              <PhoneIcon className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Phone Numbers Found</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto text-sm">
              {numbers.length === 0
                ? "You don't have any provisioned sending numbers yet. Assign and register your first WhatsApp sender number."
                : "No phone numbers match your current filters. Try selecting a different account or adjusting filters."}
            </p>
            {numbers.length === 0 && !isClient && (
              <button
                onClick={handleOpenAdd}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 text-sm cursor-pointer shadow-sm shadow-green-600/20"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Phone Number
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-14">
                      #
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Assigned Account / Client
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Sending Phone Number
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Meta WABA ID
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Business ID
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Default
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    {!isClient && (
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedNumbers.map((row, index) => {
                    const id = row._id || row.id || row.str_phone_number_id;
                    const phone = row.str_phone_number || "-";
                    const phoneId = row.str_phone_number_id || "-";
                    const wabaId = row.str_waba_id || "-";
                    const operator = row.str_operator || "meta";
                    const isBot = Boolean(row.bool_bot);
                    const status = (row.str_status || "ACTIVE").toUpperCase();

                    const accountCode =
                      row.account_info?.str_account_code ||
                      `ACC${String(row.int_account_id || 1).padStart(6, "0")}`;
                    const username = row.account_info?.str_username;
                    const accountName =
                      row.account_info?.str_name ||
                      row.account_info?.str_company_name ||
                      "Client Account";

                    return (
                      <tr
                        key={id || index}
                        className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-400">
                          #{(currentPage - 1) * numbersPerPage + index + 1}
                        </td>

                        {/* Assigned Account / Username column */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-green-50 to-emerald-100 dark:from-gray-700 dark:to-gray-800 border border-green-200 dark:border-gray-600 text-green-700 dark:text-green-300 flex items-center justify-center font-bold text-xs shadow-xs flex-shrink-0">
                              {(username || accountName).charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900 dark:text-white font-mono text-xs">
                                  {accountCode}
                                </span>
                                {username && (
                                  <span className="text-[11px] font-semibold text-green-600 dark:text-green-400 font-mono bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 rounded-md">
                                    @{username}
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[170px] mt-0.5">
                                {accountName}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Phone Number & ID */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center font-bold shadow-xs flex-shrink-0">
                              <PhoneIcon className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-white font-mono text-xs sm:text-sm">
                                {phone}
                              </div>
                              
                            </div>
                          </div>
                        </td>

                        {/* Meta WABA ID */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-lg">
                            {wabaId}
                          </span>
                        </td>

                        {/* Business ID */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-xs text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-lg">
                            {row.str_business_id || "-"}
                          </span>
                        </td>

                        {/* Default */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {row.bool_is_default ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                              ✓ Default
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                              No
                            </span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {status === "ACTIVE" ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                              ● ACTIVE
                            </span>
                          ) : status === "PENDING" ? (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                              ● PENDING
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">
                              ● INACTIVE
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        {!isClient && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-1">
                              <button
                                onClick={() => handleEdit(row)}
                                className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors duration-200 cursor-pointer"
                                title="Edit Configuration"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(row)}
                                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200 cursor-pointer"
                                title="Decommission Number"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Showing {(currentPage - 1) * numbersPerPage + 1} to{" "}
                  {Math.min(currentPage * numbersPerPage, filteredNumbers.length)} of{" "}
                  {filteredNumbers.length} numbers
                </div>

                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + Math.max(1, currentPage - 2);
                    if (pageNum > totalPages) return null;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? "bg-green-600 text-white"
                            : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Slide-over Right Sidebar Drawer for Register / Edit Phone Number ── */}
      {showDrawer && !isClient && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop overlay */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity duration-300"
            onClick={() => {
              setShowDrawer(false);
              resetForm();
            }}
          />

          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-xl bg-white dark:bg-gray-800 shadow-2xl border-l border-gray-200 dark:border-gray-700 flex flex-col justify-between animate-in slide-in-from-right duration-300">
              
              {/* Drawer Header */}
              <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-gray-50/90 to-white dark:from-gray-900/60 dark:to-gray-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white flex items-center justify-center font-bold shadow-md shadow-green-600/20 flex-shrink-0">
                    <PhoneIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      {selectedNumber ? "Edit WhatsApp Phone Number" : "Register WhatsApp Sender"}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {selectedNumber
                        ? "Update phone number gateway credentials and account assignment"
                        : "Provision and assign a new sender number to a client account"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowDrawer(false);
                    resetForm();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer Body (Scrollable Form) */}
              <form
                id="phone-number-drawer-form"
                onSubmit={handleSave}
                className="flex-1 overflow-y-auto p-6 space-y-6"
              >
                {/* 1. Account / Username Selection Section */}
                <div className="bg-gradient-to-br from-green-50/70 to-emerald-50/40 dark:from-gray-700/40 dark:to-gray-800/40 rounded-2xl p-5 border border-green-100 dark:border-gray-700 space-y-3.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-bold uppercase tracking-wider text-green-900 dark:text-green-300">
                      Assigned Account / Client <span className="text-red-500">*</span>
                    </label>
                    <span className="text-[11px] text-green-700 dark:text-green-400 font-semibold flex items-center gap-1">
                      <UserCircleIcon className="w-3.5 h-3.5" />
                      Client Binding
                    </span>
                  </div>

                  <select
                    required
                    value={formData.int_account_id}
                    onChange={(e) =>
                      setFormData({ ...formData, int_account_id: Number(e.target.value) })
                    }
                    className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm font-medium focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all shadow-sm"
                  >
                    <option value="">-- Select Client Account / Username --</option>
                    {accountOptions.map((acc) => (
                      <option key={acc.value} value={acc.value}>
                        {acc.label}
                      </option>
                    ))}
                  </select>

                  {/* Selected Account Info Card */}
                  {selectedAccountDetails && (
                    <div className="mt-2.5 p-3 rounded-xl bg-white/90 dark:bg-gray-800/90 border border-green-200/80 dark:border-gray-600/80 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-green-600 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                          {(
                            selectedAccountDetails.username ||
                            selectedAccountDetails.name ||
                            "A"
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                            <span>{selectedAccountDetails.code}</span>
                            {selectedAccountDetails.username && (
                              <span className="text-green-600 dark:text-green-400 font-mono">
                                @{selectedAccountDetails.username}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                            {selectedAccountDetails.name || selectedAccountDetails.company || "Client Account"}
                            {selectedAccountDetails.email && ` · ${selectedAccountDetails.email}`}
                          </div>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200 whitespace-nowrap">
                        Verified Account
                      </span>
                    </div>
                  )}
                </div>

                {/* 2. Sender Details Form Grid */}
                <div className="space-y-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Sender Credentials
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        WhatsApp Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="+919876543210 (E.164)"
                        value={formData.str_phone_number}
                        onChange={(e) =>
                          setFormData({ ...formData, str_phone_number: e.target.value })
                        }
                        className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Meta Phone Number ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. 58874584158514"
                        value={formData.str_phone_number_id}
                        onChange={(e) =>
                          setFormData({ ...formData, str_phone_number_id: e.target.value })
                        }
                        className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Meta WABA ID
                      </label>
                      <input
                        type="text"
                        placeholder="WhatsApp Business Account ID"
                        value={formData.str_waba_id}
                        onChange={(e) =>
                          setFormData({ ...formData, str_waba_id: e.target.value })
                        }
                        className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Business ID (str_business_id)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. 103523966079004"
                        value={formData.str_business_id}
                        onChange={(e) =>
                          setFormData({ ...formData, str_business_id: e.target.value })
                        }
                        className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Gateway Provider
                      </label>
                      <select
                        value={formData.str_operator}
                        onChange={(e) =>
                          setFormData({ ...formData, str_operator: e.target.value })
                        }
                        className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all capitalize"
                      >
                        <option value="meta">Meta Cloud API</option>
                        <option value="karix">Karix</option>
                        <option value="pinnacle">Pinnacle</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Operational Status
                      </label>
                      <select
                        value={formData.str_status}
                        onChange={(e) =>
                          setFormData({ ...formData, str_status: e.target.value })
                        }
                        className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      >
                        <option value="ACTIVE">ACTIVE</option>
                        <option value="INACTIVE">INACTIVE</option>
                        <option value="PENDING">PENDING</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Default Sending Number
                      </label>
                      <div className="flex items-center h-[42px] px-3.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700">
                        <label className="inline-flex items-center cursor-pointer w-full">
                          <input
                            type="checkbox"
                            checked={formData.bool_is_default}
                            onChange={(e) =>
                              setFormData({ ...formData, bool_is_default: e.target.checked })
                            }
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                          />
                          <span className="ml-2.5 text-xs font-medium text-gray-700 dark:text-gray-300">
                            Set as Default Number
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 3. API Token and Webhook Endpoints */}
                <div className="space-y-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Integration Credentials & Webhooks
                  </h4>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      WhatsApp / Provider Token (str_wp_token)
                    </label>
                    <input
                      type="password"
                      placeholder="Access Token / Provider Key..."
                      value={formData.str_wp_token}
                      onChange={(e) =>
                        setFormData({ ...formData, str_wp_token: e.target.value })
                      }
                      className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Application ID (str_app_id)
                    </label>
                    <input
                      type="text"
                      placeholder="Application ID (optional)"
                      value={formData.str_app_id}
                      onChange={(e) =>
                        setFormData({ ...formData, str_app_id: e.target.value })
                      }
                      className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                      Webhook Inbound URL (str_webhook_url)
                    </label>
                    <input
                      type="url"
                      placeholder="https://yourdomain.com/api/tenant/messages/incoming"
                      value={formData.str_webhook_url}
                      onChange={(e) =>
                        setFormData({ ...formData, str_webhook_url: e.target.value })
                      }
                      className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </form>

              {/* Drawer Sticky Footer with Actions */}
              <div className="p-4 sm:p-5 border-t border-gray-200 dark:border-gray-700 bg-gray-50/90 dark:bg-gray-900/60 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDrawer(false);
                    resetForm();
                  }}
                  className="px-5 py-2.5 text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-200/60 dark:hover:bg-gray-700 rounded-xl transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="phone-number-drawer-form"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-md shadow-green-600/20 text-xs sm:text-sm cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckIcon className="w-4 h-4 mr-2" />
                      {selectedNumber ? "Update Phone Number" : "Register Number"}
                    </>
                  )}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
